/**
 * API client configuration and services for the pgkb platform.
 *
 * @remarks
 * This module configures an Axios instance for making HTTP requests to the pgkb backend API.
 * It includes:
 *   - Request/response interceptors for authentication
 *   - Automatic error parsing and handling
 *   - Token management and expiration handling
 *   - Service modules for different API domains (auth, consents)
 *
 * The API client automatically includes JWT tokens from localStorage in the Authorization header
 * and handles token expiration by redirecting to the login page.
 *
 * @example
 * ```typescript
 * import { api, authService } from '@shared/services/api';
 *
 * // Login and store token
 * const result = await authService.login({ email, password });
 * if ('token' in result) {
 *   setToken(result.token);
 * }
 *
 * // Subsequent requests automatically include the token
 * const user = await api.get('/users/me');
 * ```
 */

import axios from 'axios';
import i18n from '../../i18n';
import { parseApiError, isAuthError, type AppError } from '@shared/utils/errorHandler';
import { invalidateAllShared } from './sharedResource';

const API_BASE_URL = '/api/v1';

// Extend AxiosError to include custom properties
declare module 'axios' {
  interface AxiosError {
    appError?: AppError;
    timeoutError?: boolean;
    userMessage?: string;
  }
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout for all requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token in Authorization header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send current UI language to backend for translated error messages
    config.headers['Accept-Language'] = i18n.language || 'en';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration, suspension, and timeout errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Parse error using centralized error handler
    const appError = parseApiError(error);

    // Attach parsed error to the axios error for use in components
    error.appError = appError;
    error.userMessage = appError.userMessage;

    // Handle account suspension errors
    if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED') {
      // Dispatch custom event for suspension banner to refresh
      window.dispatchEvent(new CustomEvent('account-suspended'));
      // Don't logout - user has read-only access
    }

    // Handle rate limiting (429) — both the per-organization limit and the per-IP
    // limit used by the public auth endpoints. Without this the request just failed
    // silently: pages kept their empty state and the user saw nothing at all.
    if (error.response?.status === 429) {
      const headers = error.response.headers ?? {};
      const resetHeader = headers['x-ratelimit-reset'];
      const retryAfterHeader = headers['retry-after'];
      let retryAfterSeconds: number | undefined;

      if (retryAfterHeader !== undefined && !Number.isNaN(Number(retryAfterHeader))) {
        retryAfterSeconds = Number(retryAfterHeader);
      } else if (typeof resetHeader === 'string') {
        const resetAt = Date.parse(resetHeader);
        if (!Number.isNaN(resetAt)) {
          retryAfterSeconds = Math.max(0, Math.round((resetAt - Date.now()) / 1000));
        }
      }

      window.dispatchEvent(
        new CustomEvent('api-rate-limited', {
          detail: { retryAfterSeconds, path: error.config?.url },
        })
      );
    }

    // Handle authentication errors - redirect to login or admin panel
    if (isAuthError(appError)) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Feature 717: Check if this is an impersonation session that expired
        const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';

        // Only clear token and redirect if we have one (user was logged in)
        // removeToken also drops the shared fetch cache so stale identity data
        // never survives a session change.
        removeToken();

        if (isImpersonating) {
          // Feature 706/717: Impersonation session expired - clear impersonation state
          // restore admin token, and redirect to admin customers page
          const originalAdminToken = sessionStorage.getItem('original_admin_token');

          // Clear all impersonation-related sessionStorage
          sessionStorage.removeItem('is_impersonating');
          sessionStorage.removeItem('impersonated_user_email');
          sessionStorage.removeItem('impersonated_user_name');
          sessionStorage.removeItem('impersonated_user_id');
          sessionStorage.removeItem('impersonated_by');
          sessionStorage.removeItem('impersonated_user_org_id');
          sessionStorage.removeItem('impersonated_user_org_name');
          sessionStorage.removeItem('impersonated_by_admin_email');
          sessionStorage.removeItem('impersonated_by_admin_name');
          sessionStorage.removeItem('impersonation_started_at');
          sessionStorage.removeItem('original_admin_token');

          // Restore the original admin token so admin stays logged in
          if (originalAdminToken) {
            localStorage.setItem('auth_token', originalAdminToken);
          }

          // Redirect to admin panel with impersonation expired flag
          if (window.location.pathname !== '/admin/customers') {
            window.location.href = '/admin/customers?impersonation_expired=true';
          }
        } else {
          // Regular session expired - redirect to login
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            // Add session_expired flag to URL params to show message
            window.location.href = '/login?session_expired=true';
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  accept_terms_of_service: boolean;
  accept_privacy_policy: boolean;
  accept_marketing?: boolean; // Optional
}

export interface LoginRequest {
  email: string;
  password: string;
  totp_code?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    full_name: string;
    email_verified: boolean;
  };
}

/**
 * Authentication service for user registration, login, and password management.
 *
 * @remarks
 * Provides methods for all authentication-related operations including registration,
 * login with optional MFA, email verification, password reset, and password changes.
 */
export const authService = {
  /**
   * Registers a new user account.
   *
   * @param data - Registration data including email, password, and consent acceptances
   * @param data.email - User's email address (must be unique)
   * @param data.password - User's password (minimum 12 characters with mixed case, numbers, special chars)
   * @param data.full_name - User's full name
   * @param data.accept_terms_of_service - Whether user accepts terms of service
   * @param data.accept_privacy_policy - Whether user accepts privacy policy
   * @param data.accept_marketing - Optional: Whether user accepts marketing communications
   * @returns Promise resolving to registration confirmation with user ID
   * @throws {Error} If email already exists or password doesn't meet requirements
   *
   * @example
   * ```typescript
   * const result = await authService.register({
   *   email: 'user@example.com',
   *   password: 'SecurePass123!',
   *   full_name: 'John Doe',
   *   accept_terms_of_service: true,
   *   accept_privacy_policy: true,
   * });
   * console.log('User ID:', result.user_id);
   * ```
   */
  async register(data: RegisterRequest): Promise<{ message: string; user_id: string }> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  /**
   * Authenticates a user with email and password.
   *
   * @param data - Login credentials
   * @param data.email - User's email address
   * @param data.password - User's password
   * @param data.totp_code - Optional: TOTP code if user has MFA enabled
   * @returns Promise resolving to auth response with token and user info, OR MFA challenge
   * @throws {Error} If credentials are invalid or account is suspended
   *
   * @remarks
   * If the user has MFA enabled, the response will include `requires_mfa: true`
   * and you must call login again with the `totp_code` parameter.
   *
   * @example
   * ```typescript
   * const result = await authService.login({ email, password });
   * if ('token' in result) {
   *   // Successful login
   *   setToken(result.token);
   * } else {
   *   // MFA required
   *   console.log('Enter MFA code');
   * }
   * ```
   */
  async login(
    data: LoginRequest
  ): Promise<AuthResponse | { requires_mfa: boolean; message: string }> {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  /**
   * Verifies a user's email address using a verification token.
   *
   * @param token - Email verification token sent to the user's email
   * @returns Promise resolving to confirmation message
   * @throws {Error} If token is invalid or expired
   *
   * @example
   * ```typescript
   * const result = await authService.verifyEmail(verificationToken);
   * console.log(result.message);
   * ```
   */
  async verifyEmail(token: string): Promise<{ message: string; email?: string }> {
    const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  async resendVerificationEmail(data: { email: string }): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification', data);
    return response.data;
  },

  /**
   * Initiates the password reset flow by sending a reset email.
   *
   * @param data - Email address for password reset
   * @param data.email - User's email address
   * @returns Promise resolving to confirmation message
   * @throws {Error} If email is not found
   *
   * @remarks
   * A reset token will be sent to the email address if the account exists.
   * The token can be used with {@link resetPassword} to complete the reset.
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
    const response = await api.post('/auth/forgot-password', data);
    return response.data;
  },

  /**
   * Resets a user's password using a reset token.
   *
   * @param data - Password reset data
   * @param data.token - Password reset token from forgotPassword email
   * @param data.password - New password (minimum 12 characters with mixed case, numbers, special chars)
   * @returns Promise resolving to confirmation message
   * @throws {Error} If token is invalid/expired or password doesn't meet requirements
   *
   * @example
   * ```typescript
   * await authService.resetPassword({
   *   token: resetToken,
   *   password: 'NewSecurePass123!',
   * });
   * ```
   */
  async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },

  /**
   * Changes the password for an authenticated user.
   *
   * @param data - Password change data
   * @param data.current_password - User's current password
   * @param data.new_password - New password (minimum 12 characters with mixed case, numbers, special chars)
   * @returns Promise resolving to confirmation message
   * @throws {Error} If current password is incorrect or new password doesn't meet requirements
   *
   * @remarks
   * Requires authentication. The user must be logged in to change their password.
   */
  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  /**
   * Gets the current impersonation status from the backend.
   *
   * @returns Promise resolving to impersonation status with user and admin details
   * @throws {Error} If user is not authenticated
   *
   * @remarks
   * Feature 705: This endpoint validates the JWT token's impersonated_by claim
   * and returns the current impersonation state from the server. This is more
   * reliable than relying solely on browser sessionStorage, as it validates
   * the actual token claims on the backend.
   *
   * The response includes:
   * - is_impersonating: boolean flag
   * - impersonated_user: details of the user being impersonated (if active)
   * - impersonated_by_admin: details of the admin who initiated impersonation (if active)
   *
   * @example
   * ```typescript
   * const status = await authService.getImpersonationStatus();
   * if (status.is_impersonating) {
   *   console.log('Impersonating:', status.impersonated_user.email);
   *   console.log('Admin:', status.impersonated_by_admin.email);
   * }
   * ```
   */
  async getImpersonationStatus(): Promise<{
    is_impersonating: boolean;
    impersonated_user?: {
      id: string;
      email: string;
      full_name: string;
    };
    impersonated_by_admin?: {
      id: string;
      email: string;
      full_name: string;
    };
  }> {
    const response = await api.get('/auth/impersonation-status');
    return response.data;
  },
};

// Consent interfaces
export interface Consent {
  id: string;
  type: string;
  granted_at: string;
  revoked_at?: string;
  ip_address: string;
}

export interface ConsentResponse {
  consents: Consent[];
  count: number;
}

// Consent service
export const consentService = {
  async getConsents(): Promise<ConsentResponse> {
    const response = await api.get('/compliance/consents');
    return response.data;
  },

  async revokeConsent(
    consentType: string
  ): Promise<{ message: string; type: string; revoked_at: string }> {
    const response = await api.post('/compliance/consents/revoke', { consent_type: consentType });
    return response.data;
  },
};

/**
 * Stores the JWT authentication token in localStorage.
 *
 * @param token - The JWT token received from login or registration
 *
 * @remarks
 * The token is automatically included in subsequent API requests via the
 * request interceptor configured in this module.
 *
 * @example
 * ```typescript
 * const result = await authService.login({ email, password });
 * if ('token' in result) {
 *   setToken(result.token);
 * }
 * ```
 */
export const setToken = (token: string) => {
  localStorage.setItem('auth_token', token);
};

/**
 * Retrieves the current JWT authentication token from localStorage.
 *
 * @returns The JWT token if present, or null if not logged in
 *
 * @example
 * ```typescript
 * const token = getToken();
 * if (token) {
 *   console.log('User is logged in');
 * } else {
 *   console.log('User is not logged in');
 * }
 * ```
 */
export const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

/**
 * Removes the JWT authentication token from localStorage.
 *
 * @remarks
 * Use this to log out the current user. After calling this method,
 * subsequent API requests will not include an authentication token
 * and will redirect to the login page.
 *
 * @example
 * ```typescript
 * const handleLogout = () => {
 *   removeToken();
 *   window.location.href = '/login';
 * };
 * ```
 */
export const removeToken = () => {
  localStorage.removeItem('auth_token');
  // SPA logout keeps module state alive — drop the shared profile/subscription
  // cache so the next signed-in user is never served the previous user's data.
  invalidateAllShared();
};

// Filesystem browse interfaces
export interface FilesystemNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  modified_time?: string;
}

export interface FilesystemBrowseResponse {
  path: string;
  request_id?: string;
  nodes: FilesystemNode[];
}

export interface FilesystemBrowseError {
  error: string;
  message: string;
  status?: string;
  suggestion?: string;
}

/**
 * Browse filesystem on an agent via WebSocket.
 *
 * @param agentId - The ID of the agent to browse
 * @param path - The path to browse (empty string to list drives on Windows)
 * @returns Promise resolving to filesystem nodes at the specified path
 * @throws {Error} If agent is offline, timeout occurs, or path is invalid
 *
 * @remarks
 * This function sends a request to the backend which forwards it to the agent
 * via WebSocket. The agent scans the filesystem and returns the results.
 * The request has a 10-second timeout.
 */
export const browseFilesystem = async (
  agentId: string,
  path: string
): Promise<FilesystemBrowseResponse> => {
  const response = await api.get<FilesystemBrowseResponse>(
    `/agents/${agentId}/files?path=${encodeURIComponent(path)}`
  );
  return response.data;
};

/**
 * Retrieves the XSRF (Cross-Site Request Forgery) token from cookies.
 *
 * @returns The XSRF token value, or empty string if not found
 *
 * @remarks
 * XSRF tokens are used to prevent CSRF attacks. The token is typically
 * set by the server in a cookie and should be included in state-changing
 * requests (POST, PUT, DELETE) for additional security.
 */
export const getXSRFToken = (): string => {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1] || ''
  );
};
